import { ChangeDetectionStrategy, Component, booleanAttribute, computed, input } from '@angular/core';

/**
 * Transforms icon names from dash-case to underscore_case for Material Icons compatibility.
 *
 * @example
 * mapIconName('arrow-forward') // returns 'arrow_forward'
 * mapIconName('calendar-month') // returns 'calendar_month'
 * mapIconName('delete') // returns 'delete' (no change needed)
 */
export function mapIconName(name: string): string {
  return name.replaceAll('-', '_');
}

/**
 * ZagIcon Component
 *
 * A generic icon component that wraps Material Icons, providing a consistent
 * icon naming convention across the application. Uses dash-case names that
 * map to Material Icons underscore_case names.
 *
 * The component renders the mapped icon name as text content, leveraging
 * Material Icons' ligature-based rendering approach.
 *
 * @example Basic usage (filled icons, default)
 * ```html
 * <span zagIcon="arrow-forward"></span>
 * <span zagIcon="delete"></span>
 * <span zagIcon="calendar-month"></span>
 * ```
 *
 * @example Outlined icons
 * ```html
 * <span zagIcon="settings" zagOutlined></span>
 * <span zagIcon="delete" zagOutlined></span>
 * ```
 *
 * @example Within buttons (leading icon, default)
 * ```html
 * <button zagButton>
 *   <span zagIcon="save"></span>
 *   Save
 * </button>
 * ```
 *
 * @example Within buttons (trailing icon)
 * ```html
 * <button zagButton>
 *   Next
 *   <span zagIcon="arrow-forward" zagEnd></span>
 * </button>
 * ```
 *
 * @example Both leading and trailing icons
 * ```html
 * <button zagButton>
 *   <span zagIcon="chevron-left"></span>
 *   Navigate
 *   <span zagIcon="chevron-right" zagEnd></span>
 * </button>
 * ```
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[zagIcon]',
  template: `{{ mappedIconName() }}`,
  styles: `
    :host {
      display: inline-block;
      font-style: normal;
      line-height: 1;
      text-transform: none;
      letter-spacing: normal;
      word-wrap: normal;
      white-space: nowrap;
      direction: ltr;
      -webkit-font-smoothing: antialiased;
      text-rendering: optimizeLegibility;
      -moz-osx-font-smoothing: grayscale;
      font-feature-settings: 'liga';
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-button__icon',
    '[class.material-icons]': '!zagOutlined()',
    '[class.material-icons-outlined]': 'zagOutlined()',
    '[class.zag-button__icon--end]': 'zagEnd()',
  },
})
export class ZagIcon {
  /**
   * Icon name in dash-case (e.g., 'arrow-forward', 'calendar-month').
   * Will be mapped to Material Icons underscore_case format.
   */
  readonly zagIcon = input.required<string>();

  /**
   * When true, uses the outlined icon variant instead of filled (default).
   */
  readonly zagOutlined = input(false, { transform: booleanAttribute });

  /**
   * When true, positions the icon at the end (trailing) instead of start (leading).
   * Used for button icon positioning.
   */
  readonly zagEnd = input(false, { transform: booleanAttribute });

  /**
   * Computed Material Icons name (underscore_case format)
   */
  readonly mappedIconName = computed(() => mapIconName(this.zagIcon()));
}
